<template>
  <div class="tv__tooltip">
    <!-- Icono -->
    <i
      v-if="!props.useMaterialIcon"
      ref="button"
      :class="[
        'tv__input-icon',
        'tv__input-icon-tooltip',
        props.size === 'sm' ? 'tv__input-sm' : '',
      ]"
    ></i>

    <span
      v-else
      ref="button"
      class="material-symbols-outlined notranslate tv__input-icon-tooltip"
      translate="no"
      style="margin-top: 0.1rem; font-size: 1rem"
    >
      {{ props.iconName }}
    </span>

    <!-- Tooltip -->
    <div
      ref="tooltipContent"
      class="tv__tooltip-content"
      :class="[{ 'is-visible': isVisible }, tooltipDirection]"
      :style="{ maxWidth: `${maxWidth}px` }"
    >
      <span>
        <slot name="title" />
      </span>

      <span
        class="tv__tooltip-icon-close material-symbols-outlined close-tooltip-btn notranslate"
        translate="no"
        @click="closeTooltip"
      >
        close
      </span>
      <div ref="arrow" class="tv__tooltip-arrow"></div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from "vue";

const props = defineProps({
  maxWidthTooltip: Number,
  floatTooltipDesk: String,
  floatTooltipMobile: String,
  useMaterialIcon: {
    type: Boolean,
    default: false,
  },
  iconName: {
    type: String,
    default: "help",
  },
  triggerEvent: {
    type: String,
    default: "click",
    validator: (value) => ["click", "mouseenter"].includes(value),
  },
  autoCloseTimeout: {
    type: Number,
    default: null,
  },
  size: {
    type: String,
    default: "md",
  },
});

const button = ref(null);
const tooltipContent = ref(null);
const arrow = ref(null);
const isVisible = ref(false);
const tooltipDirection = ref("");
const maxWidth = ref(null);
let autoCloseTimer = null;

const showTooltip = () => {
  isVisible.value = true;
  startAutoCloseTimer();
};

const hideTooltip = () => {
  isVisible.value = false;
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }
};

const toggleTooltip = () => {
  isVisible.value ? hideTooltip() : showTooltip();
};

const closeTooltip = () => {
  hideTooltip();
};

const updateDirectionAndMaxWidth = () => {
  if (!button.value) return;

  const sizeScreen = window.innerWidth;
  maxWidth.value = props.maxWidthTooltip || 246;
  if (sizeScreen <= 400) maxWidth.value = 200;

  // calcular espacio disponible
  const rect = button.value.getBoundingClientRect();
  const espacioArriba = rect.top;
  const espacioAbajo = window.innerHeight - rect.bottom;

  // si hay más espacio abajo que arriba → bottom, sino top
  if (espacioAbajo > espacioArriba) {
    tooltipDirection.value = "bottom";
  } else {
    tooltipDirection.value = "top";
  }

  // fallback: si se quiere forzar en mobile/desktop según props
  if (sizeScreen <= 600 && props.floatTooltipMobile) {
    tooltipDirection.value = props.floatTooltipMobile;
  } else if (props.floatTooltipDesk) {
    tooltipDirection.value = props.floatTooltipDesk;
  }
};

const startAutoCloseTimer = () => {
  if (props.autoCloseTimeout) {
    autoCloseTimer = setTimeout(() => {
      hideTooltip();
    }, props.autoCloseTimeout);
  }
};

onMounted(() => {
  window.addEventListener("resize", updateDirectionAndMaxWidth);
  updateDirectionAndMaxWidth();

  if (props.triggerEvent === "click" && button.value) {
    button.value.addEventListener("click", toggleTooltip);
  } else if (props.triggerEvent === "mouseenter" && button.value) {
    button.value.addEventListener("mouseenter", showTooltip);
    button.value.addEventListener("mouseleave", hideTooltip);
  }

  // cerrar si se hace click fuera
  document.addEventListener("click", (e) => {
    if (
      isVisible.value &&
      tooltipContent.value &&
      !button.value.contains(e.target) &&
      !tooltipContent.value.contains(e.target)
    ) {
      hideTooltip();
    }
  });

  watch(
    () => props.autoCloseTimeout,
    (newValue) => {
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
      }
      if (newValue) {
        startAutoCloseTimer();
      }
    }
  );
});

onUnmounted(() => {
  window.removeEventListener("resize", updateDirectionAndMaxWidth);
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
  }
});
</script>
